<template lang="pug">
  div
    v-dialog(v-model="showOperationalModal" max-width="550px" :persistent="loading")

      .dialogHeader
        .dialogHeaderTitle
          | Relatório Operacional
        .dialogHeaderClose
          .dialogCloseButton.mdi.mdi-close(@click="closeOperationalModal()")

      .dialogBody
        .dialogBodyContent
          .payrollField.payrollField--no__margin.payrollField--first
            label.csLabel
              span(v-html="'Período'")
            .csFields.csFields--date
              date-picker(
                v-model="dateRange"
                @change="onDateChange"
                :clearable="false"
                :disabled-date="disableDatesCondition"
                range
                valueType="format"
                format="DD/MM/YYYY"
                :lang="lang"
                class="groupUserField--date"
                :disabled="loading"
              )

          .mt-5.text-right
            sos-button(
              @buttonClick="generate()"
              color="blue"
              buttonIcon="mdi-file-document-plus"
              buttonLabel="Gerar relatório"
              buttonIconSize="18"
              size="medium"
              :loading="loading"
              :disabled="!isDateRangeValid"
            )

    confirm-dialog(
      ref="cdConfirmModal"
      :title="cdParameters.title"
      :text="cdParameters.text"
      :buttonPosition="cdParameters.buttonPosition"
      :width="cdParameters.width"
      @close="cdMessageResponse"
    )
    subscription-modal(
      :active="subscriptionModal.active"
      :titulo="subscriptionModal.title"
      :text="subscriptionModal.message"
      @close="closeSubscriptionModal"
    )
</template>

<script>
  import { mapState } from 'vuex'
  import dateTimeLanguage from '@/constants/dateTimeLanguage'
  import { freeVersion } from '@/constants'
  import { apiCheckReportStatus, apiOpenReport } from '@/services/report'
  import { formatDate, isValidDate, parseDate } from '@/mixins/utils.js'
  import SubscriptionModalMixin from '@/mixins/subscription.modal.mixin'
  import confirmDialogMixin from '@/mixins/cd.mixin.js'
  import SosButton from '@/components/SosButton'
  import ConfirmDialog from '@/modals/ConfirmDialog'
  import SubscriptionModal from '@/modals/SubscriptionModal'
  import DatePicker from 'vue2-datepicker'
  import 'vue2-datepicker/index.css'
  import moment from 'moment'
  import 'moment/locale/pt-br'

  moment.locale('pt-BR')

  const buildSubscriptionModalContent = userSession => {
    const planName = userSession.planName.name
    const talkToOurSupport = `<a href="https://api.whatsapp.com/send?phone=5513992061010&text=Olá, estou no ${planName} e não consigo gerar o relatório operacional. Preciso de ajuda." target="_blank">suporte</a>`
    const textSupport = `<p>Por favor, verifique a situação do seu plano com o nosso ${talkToOurSupport}.</p>`

    if (userSession.planOrigin === 'CUSTOM') {
      return { title: 'Verifique seu plano', message: `<p>Você está no ${planName} personalizado, mas o relatório operacional não está liberado.</p>${textSupport}` }
    } else if (userSession.planOrigin === 'COUPON') {
      return { title: 'Verifique seu plano', message: `<p>Você tem um cupom ativo e está no ${planName}, mas o relatório operacional não está liberado.</p>${textSupport}` }
    } else if (userSession.planOrigin === 'VINDI' && userSession.vindi.paymentStatus === 'paid') {
      return { title: 'Faça um upgrade', message: `<p>Você está no ${planName}.</p><p>Para gerar o relatório operacional, assine o Plano Bronze ou superior.</p><p>Para mais informações, converse o nosso ${talkToOurSupport}.</p>` }
    } else if (userSession.planOrigin === 'VINDI' && userSession.vindi.paymentStatus !== 'paid') {
      return { title: 'Verifique sua assinatura', message: `<p>Você é assinante do ${planName}, mas ele não está ativo e o relatório operacional não está liberado.</p>${textSupport}` }
    } else {
      if (!!userSession.vindi === true) {
        return { title: 'Verifique sua assinatura', message: `<p>Você assinou o ${userSession.vindi.planName.name}, mas ele expirou.</p><p>Agora você está no ${planName} e o relatório operacional não está liberado.</p>${textSupport}` }
      } else {
        return { title: 'Verifique seu plano', message: `<p>Você está no ${planName} e o relatório operacional não está liberado.</p>${textSupport}` }
      }
    }
  }

  export default {
    name: 'ExportOperationalModal',

    components: {
      SubscriptionModal,
      'sos-button': SosButton,
      'confirm-dialog': ConfirmDialog,
      'date-picker': DatePicker,
    },

    mixins: [
      SubscriptionModalMixin,
      confirmDialogMixin,
    ],

    props: {
      groupID: {
        type: String,
        required: true,
        validator: value => value.length > 0
      },
      scaleID: {
        type: String,
        default: null,
        validator: value => value === null || value.length > 0
      },
    },

    data () {
      return {
        showOperationalModal: false,
        loading: false,
        dateRange: null,
        changedDateRange: null,
        lang: dateTimeLanguage,
      }
    },

    computed: {
      ...mapState({
        userSession: state => state.app.userSession,
      }),

      isDateRangeValid() {
        return Array.isArray(this.dateRange) &&
               this.dateRange.length === 2 &&
               isValidDate(this.dateRange[0]) &&
               isValidDate(this.dateRange[1])
      }
    },

    methods: {
      disableDatesCondition (date) {
        return date < new Date(2016, 0, 1)
      },

      onDateChange(dates) {
        this.changedDateRange = dates
      },

      async open(month = 'NO', year = 'NO') {
        if (this.loading) return false

        if (freeVersion === false && this.userSession.userCapabilities.folhaPagamento !== true) {
          const subscriptionModalContent = buildSubscriptionModalContent(this.userSession)
          this.openSubscriptionModal(subscriptionModalContent.title, subscriptionModalContent.message)
          return false
        }

        this.showOperationalModal = true

        if (year !== 'NO' && month !== 'NO') {
          const firstDay = new Date(year, month, 1)
          const lastDay = new Date(year, month + 1, 0)
          this.dateRange = [formatDate(firstDay), formatDate(lastDay)]
        } else {
          this.dateRange = [
            moment().startOf('month').format('DD/MM/YYYY'),
            moment().endOf('month').format('DD/MM/YYYY'),
          ]
        }
        this.changedDateRange = this.dateRange
      },

      closeOperationalModal() {
        if (!this.loading) {
          this.showOperationalModal = false
          this.reset()
        }
      },

      reset() {
        this.loading = false
        this.dateRange = null
        this.changedDateRange = null
      },

      async generate() {
        if (this.loading || !this.isDateRangeValid) return

        const firstDateObj = parseDate(this.dateRange[0])
        const lastDateObject = parseDate(this.dateRange[1])
        const differenceInDays = Math.floor((lastDateObject - firstDateObj) / (1000 * 60 * 60 * 24))

        if (differenceInDays > 62) {
          this.showMessage('Atenção!', 'Você pode selecionar até dois meses')
          return
        }

        this.loading = true

        try {
          const { scaleID, groupID } = this
          const checkOperationName = scaleID ? 'checkoperational' : 'checkgroupoperational'
          const operationName = scaleID ? 'operationalsummary' : 'groupoperationalsummary'
          const hasScale = scaleID ? `${scaleID}/` : ''

          const checkEndpoint = `${checkOperationName}/${groupID}/${hasScale}${this.dateRange[0]}/${this.dateRange[1]}`
          const generateUrl = `${operationName}/${groupID}/${hasScale}${this.dateRange[0]}/${this.dateRange[1]}`

          const response = await apiCheckReportStatus(checkEndpoint)

          if (response.code !== 1) {
            this.showMessage(response.title, response.message)
          } else {
            apiOpenReport(generateUrl)
          }
        } catch (error) {
          this.showMessage('Erro!', 'Não foi possível gerar o relatório. Por favor, atualize a página e tente novamente ou fale com o nosso suporte.')
        }

        this.loading = false
      }
    }
  }
</script>

<style lang="scss" scoped>
  .groupUserField--date {
    width: 100%;
  }

  .payrollField--last {
    margin-bottom: 20px;
  }
</style>
